import React from "react";
import { useDisclosure } from "../../shared/hooks/use-disclosure";
import {
  isRightToLeft,
  nativeLanguageName
} from "../../shared/i18n/native-language-names";
import { ChevronIcon, PhoneIcon } from "./Icons";
import type { LanguageOption } from "../types";

interface PhoneLanguageDisclosureProps {
  readonly dialInNumber: string;
  readonly languages: readonly LanguageOption[];
}

const toTelHref = (dialInNumber: string): string =>
  `tel:${dialInNumber.replace(/[^\d+]/g, "")}`;

export function PhoneLanguageDisclosure({
  dialInNumber,
  languages
}: PhoneLanguageDisclosureProps): React.JSX.Element | null {
  const { isOpen, triggerProps, panelProps } = useDisclosure();

  // A language with no digit is one the IVR drops; listing it tells a caller to press a dead key.
  const dialable = languages.filter((language) => language.phoneDigit != null);
  if (dialable.length === 0) {
    return null;
  }

  const telHref = toTelHref(dialInNumber);

  return (
    <>
      <button className="phone-toggle la-phone__toggle" {...triggerProps}>
        <PhoneIcon />
        Listen by phone in your language
        <ChevronIcon />
      </button>

      {isOpen && (
        <div className="phones open la-phone__panel" {...panelProps}>
          <p className="ph-h">
            <PhoneIcon />
            <span>
              Call <a href={telHref}>{dialInNumber}</a> and choose your language
            </span>
          </p>
          <p className="ph-sub">
            After dialing, press the digit for your language when prompted.
          </p>

          <ul className="phone-grid">
            {dialable.map((language) => {
              const native = nativeLanguageName(
                language.shortCode,
                language.englishName
              );

              return (
                <li key={language.shortCode} className="phone-item la-phone__row">
                  <span className="nm">
                    <span
                      lang={language.shortCode}
                      dir={isRightToLeft(language.shortCode) ? "rtl" : undefined}
                    >
                      {native}
                    </span>
                    <small>{language.englishName}</small>
                  </span>
                  {/*
                    "Press 2" alone is not a usable link name out of context (WCAG 2.4.4), so the
                    language it selects travels INSIDE the link rather than beside it.
                  */}
                  <a href={telHref}>
                    Press {language.phoneDigit}
                    <span className="visually-hidden">
                      {" "}
                      for {language.englishName}
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>

          <p className="phone-foot">Standard call rates may apply.</p>
        </div>
      )}
    </>
  );
}
